import * as Yup from 'yup';
import { useFormContext } from 'react-hook-form';
import { Box } from '@mui/material';

import formatPhoneNumber from 'src/compass/shared/util/formatPhoneNumber';

import {
  CasDisplayField,
  CasEditForm,
  CasMultiRowTable,
  CasRHFSelect,
  CasRHFSwitch,
  IEditForm,
} from 'src/compass/shared/components';
import { contactMethods } from './contactPhones';

/*
  Edit the two things SCE Reports owns about a customer's contacts.

  The contacts themselves are merged from HubSpot (stg.spMergeEntityContacts), so they can be
  neither added nor removed here - hence CasMultiRowTable's fixedRows, which hides the add and
  delete controls while leaving the inputs editable. The four identity columns are shown read-only
  so the sender can see who they are about to include.
*/

/*
  Which way a preferred channel reaches someone. Mobilephone and Phone are both phone numbers, so
  they count as the SAME channel and a default set may mix them freely. Email cannot mix with
  either - a message goes out one way or the other, so the odd one out would be dropped silently.
*/
const channelOf = (preferredChannel?: string | null) => {
  if (preferredChannel === 'Email') return 'email';
  return preferredChannel ? 'phone' : null;
};

const includedByDefault = (contacts?: any[]): any[] =>
  (contacts ?? []).filter((c) => c?.defaultInclude);

/* The channel the set has settled on - the one its rows that name a channel agree upon. */
const setChannelOf = (included: any[]): string | null =>
  included.map((c: any) => channelOf(c.preferredChannel)).find(Boolean) ?? null;

/*
  How a contact is reached on a channel, or nothing. Either phone column answers for "phone":
  Mobilephone and Phone are the same channel, so holding either is enough.
*/
const addressOnChannel = (contact: any, channel: string | null) =>
  channel === 'email' ? contact.email : contact.mobilephone || contact.phone;

const Schema = Yup.object().shape({
  contacts: Yup.array()
    .of(
      Yup.object().shape({
        defaultInclude: Yup.boolean(),
        // The select only ever offers a channel the contact has an address for, so this guards
        // the request rather than the user - a stale dialog whose contact lost its mobile.
        preferredChannel: Yup.string()
          .nullable()
          .oneOf(
            ['', 'Email', 'Mobilephone', 'Phone'],
            'Pick one of the addresses on file for this contact'
          ),
      })
    )
    /*
      Without a preferred channel nothing says HOW to reach the default set, so the send falls
      back to the grower email and including someone achieves nothing. One is enough - the rest
      may be left blank and follow it, provided they are reachable that way (third test below).
    */
    .test(
      'default-needs-a-channel',
      'Select a Preferred Channel for at least one contact that is included by default',
      (contacts?: any[]) => {
        const included = includedByDefault(contacts);
        return included.length === 0 || included.some((c: any) => !!c.preferredChannel);
      }
    )
    .test(
      'defaults-share-a-channel',
      'Contacts included by default must all use email or all use phone - they cannot be mixed',
      (contacts?: any[]) => {
        const channels = new Set(
          includedByDefault(contacts)
            .map((c: any) => channelOf(c.preferredChannel))
            .filter(Boolean)
        );
        return channels.size <= 1;
      }
    )
    /*
      A contact left blank follows the set's channel, so it has to be reachable on it. Checked
      here as well as on the server because this is where it can be acted on - the offending
      contact is on screen, and the fix is to give them a channel or to stop including them.
    */
    .test('defaults-reachable-on-channel', 'placeholder', function reachable(contacts?: any[]) {
      const included = includedByDefault(contacts);
      const setChannel = setChannelOf(included);
      if (!setChannel) return true;

      const unreachable = included
        .filter((c: any) => !c.preferredChannel && !addressOnChannel(c, setChannel))
        .map((c: any) => c.name);

      if (unreachable.length === 0) return true;

      const isEmail = setChannel === 'email';
      return this.createError({
        message:
          `${unreachable.join(', ')} ${unreachable.length > 1 ? 'have' : 'has'} no ` +
          `${isEmail ? 'email address' : 'phone number'}, so cannot be included by default ` +
          `with contacts sent by ${isEmail ? 'email' : 'text'}`,
      });
    }),
});

/*
  One option per address the contact actually has; an empty column is not offered. In the order
  the Email/Phone cell beside it lists them (see contactMethods).
*/
function PreferredChannelSelect({ rowNameRef, ...props }: any) {
  const { getValues } = useFormContext();

  const row = getValues(rowNameRef) ?? {};

  const options = [
    { id: '', name: 'None' },
    ...(row.mobilephone ? [{ id: 'Mobilephone', name: `${row.mobilephone} (Mobile Phone)` }] : []),
    ...(row.phone ? [{ id: 'Phone', name: `${row.phone} (Phone)` }] : []),
    ...(row.email ? [{ id: 'Email', name: row.email }] : []),
  ];

  return (
    <CasRHFSelect
      {...props}
      options={options}
      // CasRHFSelectBase capitalizes the closed select's text, which renders an address as
      // "Kirkh@Barkcattle.Com". These options ARE addresses, so they have to read verbatim.
      sx={{ '& .MuiSelect-select': { textTransform: 'none' } }}
    />
  );
}

/*
  Every way of reaching the contact in one cell, in contactMethods' order - the numbers, then the
  email - laid out the way the Customer dialog's Contacts tab lays them out - value then the kind,
  one per line - minus its tel:/sms: links, which have no place in an editor.

  Numbers are de-duped on the last 10 digits, because a contact whose mobile and phone hold the
  same number would otherwise read as two choices. Nothing wraps: an address broken across lines
  is harder to read than one that runs past its column.
*/
function RowContactMethods({ rowNameRef }: { rowNameRef: string }) {
  const { getValues } = useFormContext();

  const row = getValues(rowNameRef) ?? {};
  const methods = contactMethods(row);

  if (!methods.length) return null;

  return (
    <Box sx={{ whiteSpace: 'nowrap' }}>
      {methods.map((method) =>
        method.kind === 'email' ? (
          <div key="email">{method.value}</div>
        ) : (
          <div key={method.suffix}>
            {formatPhoneNumber(method.value)} <i>{method.suffix}</i>
          </div>
        )
      )}
    </Box>
  );
}

/* A read-only cell reading one field out of its own row (CasMultiRowTable hands a display column its rowNameRef). */
function RowValue({ rowNameRef, field }: { rowNameRef: string; field: string }) {
  const { getValues } = useFormContext();

  return getValues(`${rowNameRef}.${field}`) || '';
}

const displayValue =
  (field: string) =>
  ({ rowNameRef }: any) => <RowValue rowNameRef={rowNameRef} field={field} />;

export default function CustomerContactsForm({
  row,
  onSaved,
  editFormAdditionalData,
}: IEditForm) {
  const { insuredName } = editFormAdditionalData;

  return (
    <CasEditForm
      defaultValues={row}
      entity={editFormAdditionalData?.entity ?? 'SceContact'}
      onSaved={(data: any) => onSaved(data.contacts)}
      schema={Schema}
      mode="onSubmit"
    >
      <CasDisplayField label="Customer" value={row?.customerName || insuredName} />
      {/*
        CasMultiRowTable sets verticalAlign: 'super' on every row, which suits a grid of equal
        height inputs but leaves the read-only text sitting at the top of rows made tall by the
        switch and the select - and taller still when a long phone# wraps. Corrected here rather
        than in the component, which SCE Entry shares.
      */}
      <Box sx={{ width: '100%', '& tbody td.MuiTableCell-root': { verticalAlign: 'middle' } }}>
        <CasMultiRowTable
          name="contacts"
          width="100%"
          fixedRows
          columns={[
            {
              type: 'display',
              label: 'Name',
              name: 'name',
              value: displayValue('name'),
              align: 'left',
              width: '22%',
            },
            // The two editable columns sit next to Name: they are what this dialog is for, and the
            // identity columns after them are reference, read in passing to confirm the address.
            {
              type: 'component',
              label: 'Include by default on msgs',
              name: 'defaultInclude',
              component: CasRHFSwitch,
              width: '15%',
            },
            {
              type: 'component',
              label: 'Preferred',
              name: 'preferredChannel',
              component: PreferredChannelSelect,
              width: '24%',
            },
            {
              type: 'display',
              label: 'Email/Phone',
              name: 'email',
              value: ({ rowNameRef }: any) => <RowContactMethods rowNameRef={rowNameRef} />,
              align: 'left',
              width: '30%',
            },
          ]}
        />
      </Box>
    </CasEditForm>
  );
}
